import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { projectsApi, projectPulseApi } from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  ArrowLeft, Clock, Users, FileText, ShoppingCart, Receipt,
  TrendingUp, TrendingDown, CheckCircle2, AlertCircle, DollarSign,
  FolderKanban, Percent, Activity,
} from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_bc07a5b0-4401-454c-9821-20d81dadd6da/artifacts/21c8lw1c_fa793a21-0229-4214-a16b-e1c0df8a86d3.png";

const ProjectDetail = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { isAdmin, canManage } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pulse, setPulse] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const res = await projectsApi.getDetails(projectId);
      setData(res.data);
    } catch {
      navigate("/projects");
    } finally {
      setLoading(false);
    }
  }, [projectId, navigate]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    if (isAdmin && projectId) {
      projectPulseApi.get(projectId).then(res => setPulse(res.data)).catch(() => {});
    }
  }, [isAdmin, projectId]);

  if (loading) {
    return (
      <div className="space-y-6" data-testid="project-detail-loading">
        <div className="h-8 w-48 skeleton" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[1,2,3,4].map(i => <div key={i} className="card-surface p-5 h-24 skeleton" />)}
        </div>
        <div className="card-surface h-64 skeleton" />
      </div>
    );
  }

  if (!data) return null;
  const { project, financials } = data;

  return (
    <div className="space-y-6 slide-up" data-testid="project-detail-page">
      {/* Header */}
      <div className="flex items-start gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/projects")}
          className="text-white/40 hover:text-white mt-1" data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl sm:text-3xl font-bold text-white">{project.name}</h1>
            {project.work_number && (
              <Badge className="bg-[#6dbf4a]/15 text-[#6dbf4a] border-[#6dbf4a]/30 font-mono text-xs" variant="outline">
                {project.work_number}
              </Badge>
            )}
            <Badge className={project.is_active ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" : "bg-red-500/15 text-red-400 border-red-500/30"} variant="outline">
              {project.is_active ? "Aktiv" : "Inaktiv"}
            </Badge>
          </div>
          <div className="flex items-center gap-4 mt-1.5 text-sm text-white/40">
            {project.client_name && <span>{project.client_name}</span>}
            {project.address && <span className="text-white/25">· {project.address}</span>}
          </div>
        </div>
        <img src={LOGO_URL} alt="Mälair" className="w-12 h-auto hidden sm:block" style={{ mixBlendMode: "lighten" }} />
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="card-surface-hover cursor-pointer" onClick={() => navigate('/time-entry')} data-testid="stat-hours">
          <CardContent className="p-5">
            <div className="w-9 h-9 rounded-xl bg-[#6dbf4a]/10 flex items-center justify-center mb-3">
              <Clock className="w-4 h-4 text-[#6dbf4a]" />
            </div>
            <p className="stat-number text-2xl text-white">{data.total_hours}<span className="text-sm text-white/40 ml-0.5">h</span></p>
            <p className="text-xs text-white/40 mt-1">Totala timmar</p>
            <div className="flex gap-2 mt-2">
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400">{data.approved_hours}h godkänd</span>
              {data.pending_hours > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400">{data.pending_hours}h väntande</span>}
            </div>
          </CardContent>
        </Card>

        <Card className="card-surface-hover cursor-pointer" onClick={() => navigate('/documents')} data-testid="stat-docs">
          <CardContent className="p-5">
            <div className="w-9 h-9 rounded-xl bg-blue-500/10 flex items-center justify-center mb-3">
              <FileText className="w-4 h-4 text-blue-400" />
            </div>
            <p className="stat-number text-2xl text-white">{data.document_count}</p>
            <p className="text-xs text-white/40 mt-1">Dokument</p>
          </CardContent>
        </Card>

        <Card className="card-surface-hover cursor-pointer" onClick={() => navigate('/shopping')} data-testid="stat-shopping">
          <CardContent className="p-5">
            <div className="w-9 h-9 rounded-xl bg-purple-500/10 flex items-center justify-center mb-3">
              <ShoppingCart className="w-4 h-4 text-purple-400" />
            </div>
            <p className="stat-number text-2xl text-white">{data.shopping_done}<span className="text-sm text-white/40">/{data.shopping_total}</span></p>
            <p className="text-xs text-white/40 mt-1">Inköp klara</p>
          </CardContent>
        </Card>

        {isAdmin ? (
          <Card className="card-surface-hover cursor-pointer" onClick={() => navigate('/invoices')} data-testid="stat-invoices">
            <CardContent className="p-5">
              <div className="w-9 h-9 rounded-xl bg-red-500/10 flex items-center justify-center mb-3">
                <Receipt className="w-4 h-4 text-red-400" />
              </div>
              <p className="stat-number text-2xl text-white">{data.invoice_count}</p>
              <p className="text-xs text-white/40 mt-1">Fakturor</p>
              {data.invoice_total > 0 && <p className="text-xs text-[#6dbf4a] mt-1">{data.invoice_total.toLocaleString("sv-SE")} kr</p>}
            </CardContent>
          </Card>
        ) : (
          <Card className="card-surface" data-testid="stat-users">
            <CardContent className="p-5">
              <div className="w-9 h-9 rounded-xl bg-amber-500/10 flex items-center justify-center mb-3">
                <Users className="w-4 h-4 text-amber-400" />
              </div>
              <p className="stat-number text-2xl text-white">{data.by_user?.length || 0}</p>
              <p className="text-xs text-white/40 mt-1">Medarbetare</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Financial Overview (Admin only) */}
      {isAdmin && financials && (
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <Card className="card-surface overflow-hidden relative">
            <div className="absolute top-0 left-0 w-full h-1 bg-[#6dbf4a]" />
            <CardContent className="pt-5 pb-4">
              <p className="text-xs uppercase tracking-wider text-white/40 mb-1">Intäkter</p>
              <p className="stat-number text-xl text-white">{financials.total_revenue.toLocaleString("sv-SE")} kr</p>
            </CardContent>
          </Card>
          <Card className="card-surface overflow-hidden relative">
            <div className="absolute top-0 left-0 w-full h-1 bg-red-500" />
            <CardContent className="pt-5 pb-4">
              <p className="text-xs uppercase tracking-wider text-white/40 mb-1">Lönekostnad</p>
              <p className="stat-number text-xl text-red-400">{financials.total_cost.toLocaleString("sv-SE")} kr</p>
            </CardContent>
          </Card>
          <Card className="card-surface overflow-hidden relative">
            <div className={`absolute top-0 left-0 w-full h-1 ${financials.profit >= 0 ? "bg-emerald-500" : "bg-orange-500"}`} />
            <CardContent className="pt-5 pb-4">
              <p className="text-xs uppercase tracking-wider text-white/40 mb-1">Vinst</p>
              <p className={`stat-number text-xl ${financials.profit >= 0 ? "text-emerald-400" : "text-orange-400"}`}>
                {financials.profit >= 0 ? "+" : ""}{financials.profit.toLocaleString("sv-SE")} kr
              </p>
            </CardContent>
          </Card>
          <Card className="card-surface overflow-hidden relative">
            <div className={`absolute top-0 left-0 w-full h-1 ${financials.margin >= 0 ? "bg-emerald-500" : "bg-orange-500"}`} />
            <CardContent className="pt-5 pb-4">
              <p className="text-xs uppercase tracking-wider text-white/40 mb-1">Marginal</p>
              <p className={`stat-number text-xl ${financials.margin >= 0 ? "text-emerald-400" : "text-orange-400"} flex items-center gap-1.5`}>
                {financials.margin >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                {financials.margin}%
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Team & Invoice Status */}
      <div className={`grid grid-cols-1 ${isAdmin ? 'lg:grid-cols-2' : ''} gap-4`}>
        {/* Hours by Team Member */}
        <Card className="card-surface">
          <CardContent className="p-5">
            <h3 className="text-sm font-semibold text-white/40 uppercase tracking-wider mb-4 flex items-center gap-2">
              <Users className="w-4 h-4 text-[#6dbf4a]" /> Timmar per medarbetare
            </h3>
            <div className="space-y-2">
              {data.by_user?.map(u => {
                const maxH = Math.max(...data.by_user.map(x => x.hours), 1);
                const pct = (u.hours / maxH) * 100;
                return (
                  <div key={u.user_id} className="flex items-center gap-3">
                    <span className="text-sm text-white w-32 truncate">{u.name}</span>
                    <div className="flex-1 h-5 bg-white/[0.04] rounded-md overflow-hidden">
                      <div className="h-full bg-[#6dbf4a]/60 rounded-md" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-sm text-white/40 font-mono w-14 text-right">{u.hours.toFixed(1)}h</span>
                  </div>
                );
              })}
              {(!data.by_user || data.by_user.length === 0) && (
                <p className="text-sm text-white/25 text-center py-4">Inga timmar rapporterade</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Invoice Status (Admin) */}
        {isAdmin && financials && (
          <Card className="card-surface">
            <CardContent className="p-5">
              <h3 className="text-sm font-semibold text-white/40 uppercase tracking-wider mb-4 flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-emerald-400" /> Faktureringsstatus
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-white/40">Fakturerade timmar</span>
                  <span className="text-sm text-emerald-400 font-medium flex items-center gap-1">
                    <CheckCircle2 className="w-3.5 h-3.5" /> {financials.invoiced_hours}h
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-white/40">Ej fakturerade timmar</span>
                  <span className="text-sm text-amber-400 font-medium flex items-center gap-1">
                    <AlertCircle className="w-3.5 h-3.5" /> {financials.not_invoiced_hours}h
                  </span>
                </div>
                {financials.not_invoiced_hours > 0 && (
                  <div className="pt-2">
                    <div className="h-3 bg-white/[0.04] rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-500/70 rounded-full"
                        style={{ width: `${data.approved_hours > 0 ? (financials.invoiced_hours / data.approved_hours * 100) : 0}%` }} />
                    </div>
                    <p className="text-xs text-white/25 mt-1.5 text-right">
                      {data.approved_hours > 0 ? Math.round(financials.invoiced_hours / data.approved_hours * 100) : 0}% fakturerat
                    </p>
                  </div>
                )}
                <div className="flex items-center justify-between pt-2 border-t border-white/[0.06]">
                  <span className="text-sm text-white/40">Timpris</span>
                  <span className="text-sm text-white font-medium">{financials.hourly_rate} kr/h</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Project Pulse - Live Cost Meter (Admin only) */}
      {isAdmin && pulse && pulse.timeline?.length > 0 && (
        <Card className="card-surface" data-testid="project-pulse">
          <CardContent className="p-5">
            <h3 className="text-sm font-semibold text-white/40 uppercase tracking-wider mb-4 flex items-center gap-2">
              <Activity className="w-4 h-4 text-[#6dbf4a]" /> Live Projektkostnad
            </h3>

            {/* Pulse stats row */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5">
              <div className="bg-[#080c12] rounded-xl p-3">
                <p className="text-[10px] uppercase tracking-wider text-white/25">Timmar</p>
                <p className="stat-number text-lg text-white">{pulse.total_hours}h</p>
              </div>
              <div className="bg-[#080c12] rounded-xl p-3">
                <p className="text-[10px] uppercase tracking-wider text-white/25">Intakt</p>
                <p className="stat-number text-lg text-white">{pulse.total_revenue?.toLocaleString("sv-SE")} kr</p>
              </div>
              <div className="bg-[#080c12] rounded-xl p-3">
                <p className="text-[10px] uppercase tracking-wider text-white/25">Kostnad</p>
                <p className="stat-number text-lg text-red-400">{pulse.total_cost?.toLocaleString("sv-SE")} kr</p>
              </div>
              <div className="bg-[#080c12] rounded-xl p-3">
                <p className="text-[10px] uppercase tracking-wider text-white/25">Vinst</p>
                <p className={`stat-number text-lg ${pulse.total_profit >= 0 ? "text-emerald-400" : "text-orange-400"}`}>
                  {pulse.total_profit >= 0 ? "+" : ""}{pulse.total_profit?.toLocaleString("sv-SE")} kr
                </p>
              </div>
              <div className="bg-[#080c12] rounded-xl p-3">
                <p className="text-[10px] uppercase tracking-wider text-white/25">Marginal</p>
                <p className={`stat-number text-lg flex items-center gap-1 ${pulse.margin >= 0 ? "text-emerald-400" : "text-orange-400"}`}>
                  {pulse.margin >= 0 ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                  {pulse.margin}%
                </p>
              </div>
            </div>

            {/* Budget bar */}
            {pulse.budget_used_pct !== null && pulse.budget_hours > 0 && (
              <div className="mb-5">
                <div className="flex items-center justify-between text-xs text-white/40 mb-1.5">
                  <span>Budget: {pulse.total_hours} / {pulse.budget_hours}h</span>
                  <span className={pulse.budget_used_pct > 90 ? "text-red-400 font-bold" : pulse.budget_used_pct > 70 ? "text-amber-400" : "text-emerald-400"}>
                    {pulse.budget_used_pct}%
                  </span>
                </div>
                <div className="h-3 bg-white/[0.04] rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${
                      pulse.budget_used_pct > 90 ? "bg-red-500" : pulse.budget_used_pct > 70 ? "bg-amber-500" : "bg-[#6dbf4a]"
                    }`}
                    style={{ width: `${Math.min(pulse.budget_used_pct, 100)}%` }}
                  />
                </div>
              </div>
            )}

            {/* Timeline chart */}
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={pulse.timeline} margin={{ left: -10, right: 10, top: 5 }}>
                <defs>
                  <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6dbf4a" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#6dbf4a" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="costGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ef4444" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                <XAxis
                  dataKey="date"
                  stroke="#666"
                  fontSize={10}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v) => { const parts = v.split('-'); return `${parts[2]}/${parts[1]}`; }}
                />
                <YAxis
                  tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}
                  stroke="#666"
                  fontSize={10}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip
                  contentStyle={{ backgroundColor: '#12161e', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '12px', padding: '10px 14px' }}
                  labelStyle={{ color: '#fff', fontWeight: 600, marginBottom: 4 }}
                  formatter={(value, name) => {
                    const labels = { cum_revenue: 'Intakt', cum_cost: 'Kostnad', cum_profit: 'Vinst' };
                    return [`${value?.toLocaleString('sv-SE')} kr`, labels[name] || name];
                  }}
                  labelFormatter={(label) => { const p = label.split('-'); return `${p[2]}/${p[1]} ${p[0]}`; }}
                />
                <Area type="monotone" dataKey="cum_revenue" stroke="#6dbf4a" strokeWidth={2} fill="url(#revGrad)" name="cum_revenue" />
                <Area type="monotone" dataKey="cum_cost" stroke="#ef4444" strokeWidth={2} fill="url(#costGrad)" name="cum_cost" />
                <Area type="monotone" dataKey="cum_profit" stroke="#22c55e" strokeWidth={2} fill="none" strokeDasharray="5 3" name="cum_profit" />
              </AreaChart>
            </ResponsiveContainer>

            <div className="flex items-center gap-5 mt-3 pt-3 border-t border-white/[0.06]">
              <span className="flex items-center gap-1.5 text-xs text-white/30"><div className="w-3 h-1 rounded bg-[#6dbf4a]" /> Intakt</span>
              <span className="flex items-center gap-1.5 text-xs text-white/30"><div className="w-3 h-1 rounded bg-red-500" /> Kostnad</span>
              <span className="flex items-center gap-1.5 text-xs text-white/30"><div className="w-3 h-1 rounded bg-emerald-500" style={{ borderBottom: '1px dashed #22c55e' }} /> Vinst</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Navigation */}
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" className="border-white/[0.06] text-white/40 hover:text-white gap-2" onClick={() => navigate('/time-entry')} data-testid="go-time-entry">
          <Clock className="w-4 h-4" /> Rapportera tid
        </Button>
        <Button variant="outline" className="border-white/[0.06] text-white/40 hover:text-white gap-2" onClick={() => navigate('/documents')} data-testid="go-documents">
          <FileText className="w-4 h-4" /> Dokument
        </Button>
        <Button variant="outline" className="border-white/[0.06] text-white/40 hover:text-white gap-2" onClick={() => navigate('/shopping')} data-testid="go-shopping">
          <ShoppingCart className="w-4 h-4" /> Inköpslista
        </Button>
        {isAdmin && (
          <Button variant="outline" className="border-white/[0.06] text-white/40 hover:text-white gap-2" onClick={() => navigate('/reports')} data-testid="go-reports">
            <Receipt className="w-4 h-4" /> Rapporter
          </Button>
        )}
      </div>
    </div>
  );
};

export default ProjectDetail;
